<script setup>
// Reviewed recipient list. Contacts without a usable phone are split into their own
// collapsed group: they are never dispatched, but hiding them entirely would make the
// segment count look wrong to the operator.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  contacts: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
});

const emit = defineEmits(['edit', 'remove']);

const { t } = useI18n();
const search = ref('');
// Sendable rows are the ones being reviewed, so that group opens; the unsendable ones are
// there to explain a count gap, not to be read line by line.
const showSendable = ref(true);
const showMissing = ref(false);

function matches(contact, term) {
  return `${contact.first_name} ${contact.last_name} ${contact.email} ${contact.phone} ${contact.company}`
    .toLowerCase()
    .includes(term);
}

const filtered = computed(() => {
  const term = search.value.trim().toLowerCase();
  if (!term) return props.contacts;
  return props.contacts.filter(contact => matches(contact, term));
});

const sendable = computed(() => filtered.value.filter(c => c.has_phone));
const missing = computed(() => filtered.value.filter(c => !c.has_phone));

function fullName(contact) {
  return [contact.first_name, contact.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
}
</script>

<template>
  <div>
    <input
      v-model="search"
      type="search"
      class="w-full px-3 py-2 mb-3 text-sm border rounded-lg reset-base border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-n-brand"
      :placeholder="t('OUTBOUNDS.CONTACTS.SEARCH')"
    />

    <p v-if="loading" class="text-sm text-n-slate-10">
      {{ t('OUTBOUNDS.CONTACTS.LOADING') }}
    </p>

    <p v-else-if="!contacts.length" class="text-sm text-n-slate-10">
      {{ t('OUTBOUNDS.CONTACTS.EMPTY') }}
    </p>

    <template v-else>
      <button
        type="button"
        class="flex items-center justify-between w-full gap-3 px-4 py-3 border rounded-xl border-n-teal-6 bg-n-teal-2"
        @click="showSendable = !showSendable"
      >
        <span
          class="flex items-center gap-2 text-sm font-medium text-n-teal-11"
        >
          <i class="i-lucide-check size-4" />
          {{ t('OUTBOUNDS.CONTACTS.WITH_PHONE') }}
          <span
            class="px-2 py-0.5 text-xs tabular-nums rounded-full bg-n-teal-3 text-n-teal-11"
          >
            {{ sendable.length }}
          </span>
        </span>
        <i
          class="size-4 text-n-teal-11"
          :class="
            showSendable ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
          "
        />
      </button>

      <div
        v-if="showSendable"
        class="mt-2 overflow-x-auto border rounded-lg border-n-weak"
      >
        <table class="w-full text-sm">
          <thead class="bg-n-alpha-1 text-n-slate-11">
            <tr>
              <th class="w-10 px-3 py-2 font-medium text-left">#</th>
              <th class="px-3 py-2 font-medium text-left">
                {{ t('OUTBOUNDS.CONTACTS.NAME') }}
              </th>
              <th class="px-3 py-2 font-medium text-left">
                {{ t('OUTBOUNDS.CONTACTS.PHONE') }}
              </th>
              <th class="px-3 py-2 font-medium text-left">
                {{ t('OUTBOUNDS.CONTACTS.EMAIL') }}
              </th>
              <th class="px-3 py-2 font-medium text-left">
                {{ t('OUTBOUNDS.CONTACTS.COMPANY') }}
              </th>
              <th class="px-3 py-2 w-20" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(contact, index) in sendable"
              :key="contact.contact_id"
              class="border-t border-n-weak"
            >
              <td class="px-3 py-2 tabular-nums text-n-slate-10">
                {{ index + 1 }}
              </td>
              <td class="px-3 py-2 text-n-slate-12">
                {{ fullName(contact) || '—' }}
              </td>
              <td class="px-3 py-2 tabular-nums text-n-slate-11">
                {{ contact.phone }}
              </td>
              <td class="px-3 py-2 truncate max-w-48 text-n-slate-11">
                {{ contact.email || '—' }}
              </td>
              <td class="px-3 py-2 truncate max-w-40 text-n-slate-11">
                {{ contact.company || '—' }}
              </td>
              <td class="px-3 py-2">
                <div class="flex items-center justify-end gap-1">
                  <button
                    type="button"
                    class="p-1.5 rounded-md text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1"
                    :title="t('OUTBOUNDS.CONTACTS.EDIT')"
                    @click="emit('edit', contact)"
                  >
                    <i class="i-lucide-pencil size-4" />
                  </button>
                  <button
                    type="button"
                    class="p-1.5 rounded-md text-n-slate-10 hover:text-n-ruby-11 hover:bg-n-alpha-1"
                    :title="t('OUTBOUNDS.CONTACTS.REMOVE')"
                    @click="emit('remove', contact)"
                  >
                    <i class="i-lucide-trash-2 size-4" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="missing.length" class="mt-3">
        <button
          type="button"
          class="flex items-center justify-between w-full gap-3 px-4 py-3 border rounded-xl border-n-ruby-6 bg-n-ruby-2"
          @click="showMissing = !showMissing"
        >
          <span
            class="flex items-center gap-2 text-sm font-medium text-n-ruby-11"
          >
            <i class="i-lucide-x size-4" />
            {{ t('OUTBOUNDS.CONTACTS.WITHOUT_PHONE') }}
            <span
              class="px-2 py-0.5 text-xs tabular-nums rounded-full bg-n-ruby-3 text-n-ruby-11"
            >
              {{ missing.length }}
            </span>
          </span>
          <i
            class="size-4 text-n-ruby-11"
            :class="
              showMissing ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
            "
          />
        </button>

        <ul v-if="showMissing" class="mt-2 space-y-1">
          <li
            v-for="contact in missing"
            :key="contact.contact_id"
            class="flex items-center justify-between px-3 py-2 text-sm rounded-lg bg-n-alpha-1"
          >
            <span class="text-n-slate-11">
              {{ fullName(contact) || contact.email || contact.contact_id }}
            </span>
            <button
              type="button"
              class="text-xs text-n-brand hover:underline"
              @click="emit('edit', contact)"
            >
              {{ t('OUTBOUNDS.CONTACTS.ADD_PHONE') }}
            </button>
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>
